import { useEffect, useRef, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { endSession, getLiveState, startSession } from '../data/api';

/* Start or end the class's live session from Buffy's chat (2026-10-06). It
   reads the session state fresh (getLiveState) before offering anything, the
   way Live Monitor refuses to act on a stale view, then uses the page's own
   calls and words: Start class session, or End session with its two-step
   "Stop observing this session?" confirm. */
export default function LiveSessionDialog({ auth, classId, onClose, onDone }) {
  const [state, setState] = useState({ loading: true });
  const [busy, setBusy] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);
  useEffect(() => {
    let alive = true;
    getLiveState(auth, classId)
      .then((live) => { if (alive) setState({ session: live?.session || null }); })
      .catch(() => { if (alive) setState({ failed: true }); });
    return () => { alive = false; clearTimeout(timer.current); };
  }, [auth, classId]);
  const act = async (kind) => {
    setBusy(true); setError('');
    try {
      if (kind === 'start') await startSession(auth, classId); else await endSession(auth, classId);
      onDone(kind === 'start' ? 'Started a live class session.' : 'Ended the live class session.', kind === 'start' ? 'Live session started' : 'Live session ended');
      onClose();
    } catch (e) {
      setError(e?.message || 'That didn’t go through. Try again.');
    } finally { setBusy(false); }
  };
  const end = () => {
    if (!confirmEnd) { setConfirmEnd(true); clearTimeout(timer.current); timer.current = setTimeout(() => setConfirmEnd(false), 4000); return; }
    act('end');
  };
  const startedMins = state.session?.startedAt ? Math.max(0, Math.round((Date.now() - Date.parse(state.session.startedAt)) / 60000)) : null;
  const status = state.loading ? 'Checking whether a session is running…'
    : state.failed ? 'Couldn’t check the live session. Try again in a moment.'
      : state.session ? `A live session is running${startedMins != null ? ` (started ${startedMins} min ago)` : ''}.`
        : 'No live session is running for this class.';
  return (
    <Modal open onClose={busy ? () => {} : onClose} size="sm">
      <ModalHeader title="Live session" pad={DLG.head} onClose={busy ? () => {} : onClose} />
      <div className={DLG.body}>
        <p role="status" className="m-0 text-[13px] text-neutral-700 leading-relaxed flex items-center gap-2">
          {state.loading && <Loader2 size={14} className="animate-spin flex-none" aria-hidden="true" />}
          {status}
        </p>
        {!state.loading && !state.failed && !state.session && (
          <p className="mt-2 mb-0 text-[12.5px] text-neutral-500">Live Monitor then shows who is working, stuck or idle, updating about every 15 seconds.</p>
        )}
        {error && <p role="alert" className="mt-[12px] mb-0 text-[12.5px] font-semibold text-error-600">{error}</p>}
      </div>
      <div className={DLG.foot}>
        <Button variant="secondary" size="dlg" onClick={onClose} disabled={busy}>{state.loading || state.failed ? 'Close' : 'Cancel'}</Button>
        {!state.loading && !state.failed && !state.session && (
          <Button variant="primary" size="dlg" onClick={() => act('start')} disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Play size={14} aria-hidden="true" />} {busy ? 'Starting…' : 'Start class session'}
          </Button>
        )}
        {!state.loading && !state.failed && state.session && (
          <Button variant="primary" size="dlg" onClick={end} disabled={busy}>
            {busy ? 'Ending…' : confirmEnd ? 'Stop observing this session?' : 'End session'}
          </Button>
        )}
      </div>
    </Modal>
  );
}
